import Guid from "_shared/guids/guid";
import Topic from "messaging/forums/topics/topic";
import AHDate from "_shared/dates/date";
import EntityMember from "_shared/entity-members/entity-member";
import ForumSchema, { ForumJSON, ForumParams } from "messaging/forums/_schemas/forum-schema";
import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import ForumName from "notifications/message-notifications/forum-info/forum-name";
import { ForumInfoJSON } from "notifications/message-notifications/forum-info/forum-info-schema";
import GuidSchema from "_shared/guids/guid-schema";
import ForumEntityClassSchema from "notifications/message-notifications/forum-info/forum-entity-class-schema";

/** The topic names of a proposal's forums, lower-cased: today's and the legacy one. */
const proposalEntityClasses = new Set(["work.proposal", "proposal"]);

export default class Forum {
  public static readonly EXAMPLE: Forum = new Forum({
    id: Guid.generate(),
    name: new ForumName("General Discussion"),
    topic: Topic.EXAMPLE,
    subscribers: [EntityMember.EXAMPLE],
    creator: EntityMember.EXAMPLE,
    createdDate: new AHDate(),
    numUnreadMessages: 5,
  });

  public static create(params: ForumParams): Forum {
    ForumSchema.validate(params);
    return new Forum(params);
  }

  public static fromJSON(json: ForumJSON): Forum {
    const parsed = ForumSchema.deserialize(json);
    return new Forum(parsed);
  }

  public static fromJSONArray(jsonArray: unknown): Forum[] | null | undefined {
    if (isNullOrUndefined(jsonArray)) return jsonArray;
    const parsedArray = ForumSchema.deserializeJSONArray(jsonArray);
    return parsedArray.map((parsed) => new Forum(parsed));
  }

  public readonly name: ForumName;
  public readonly topic?: Topic;
  public readonly subscribers?: EntityMember[];
  public readonly id?: Guid;
  public readonly creator?: EntityMember;
  public readonly createdDate?: AHDate;
  public readonly numUnreadMessages?: number;
  /**
   * When the reader's view of this forum stops: they left the work it belongs to, read what was
   * said while they were on it, and are told nothing new (#5833). Undefined for a reader who reads
   * it as it is.
   */
  public readonly visibleUntil?: AHDate;
  /** False when nobody may write in the forum any more, because the work it belongs to has ended. */
  public readonly acceptsMessages?: boolean;
  /**
   * False when the reader may read the forum and not write in it: somebody asked to lead a team who
   * has not accepted yet (#6094), or somebody who left. Undefined when the server did not say.
   */
  public readonly callerMayWrite?: boolean;

  /** Whether the reader may still write in the forum: it is open, they have not left it, and they may write. */
  public get takesMessagesFromReader(): boolean {
    return this.acceptsMessages !== false && !this.visibleUntil && this.callerMayWrite !== false;
  }

  public constructor(params: ForumParams) {
    ForumSchema.validate(params);
    Object.assign(this, params);
    this.name = params.name;
  }

  public toJSON(): ForumJSON {
    return ForumSchema.serialize(this);
  }

  public toForumInfoJSON(): ForumInfoJSON {
    return {
      id: GuidSchema.validateJSON(this.id?.toJSON()),
      name: this.name.toJSON(),
      entityClass: ForumEntityClassSchema.validateJSON(this.topic?.entityClass?.toJSON()),
      entityId: this.topic?.entityId?.toJSON() ?? null,
    };
  }

  /**
   * Whether a message here may carry files. A proposal's comments carry text only (#5819), and the
   * server refuses a file on any proposal forum, wherever it is written from. Both topic spellings
   * name a proposal, in any case, as the server reads them.
   */
  public acceptsAttachments(): boolean {
    const entityClass = this.topic?.entityClass?.value.toLocaleLowerCase();
    return !entityClass || !proposalEntityClasses.has(entityClass);
  }

  public isEqualTo(other: Forum): boolean {
    if (!other) return false;
    return this.id?.isEqualTo(other.id ?? null) ?? false;
  }

  public toString(): string {
    return `Forum { 
      id: ${this.id?.toString() ?? "undefined"}, 
      name: ${this.name.toString()} 
    }`;
  }
}

export class ForumDoesNotExistError extends Error {
  constructor(id?: Guid | string) {
    if (id instanceof Guid) {
      super(`Forum ${id} does not exist`);
    } else {
      super(`Forum does not exist: ${id}`);
    }
  }
}

export class UnauthorizedForumAccessException extends Error {
  constructor(forumId: Guid) {
    super(`User is not authorized to access forum ${forumId}`);
  }
}
